import React from 'react';
import { Link } from 'react-router-dom';
import { Award, Users, Globe, TrendingUp, Target, Heart, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';

const AboutUs = () => {
  const values = [
    {
      icon: Target,
      title: 'Our Mission',
      description: 'To simplify UAE business expansion for entrepreneurs worldwide by providing comprehensive, hassle-free setup services from our Chennai office.'
    },
    {
      icon: Heart,
      title: 'Our Vision',
      description: 'To become the most trusted partner for businesses expanding into the UAE, known for excellence, transparency, and client success.'
    },
    {
      icon: Users,
      title: 'Client-Centric',
      description: 'We put our clients first, providing personalized solutions tailored to their unique business needs and goals.'
    },
    {
      icon: Award,
      title: 'Excellence',
      description: 'We maintain the highest standards of service quality and professionalism in every aspect of our work.'
    }
  ];

  const stats = [
    { number: '15+', label: 'Years Experience' },
    { number: '5000+', label: 'Companies Setup' },
    { number: '98%', label: 'Client Satisfaction' },
    { number: '50+', label: 'Team Members' }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-[#6B1B4D] to-[#2D1B3D] text-white py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl">
            <h1 className="text-5xl font-bold mb-6">About Firmzy</h1>
            <p className="text-xl leading-relaxed">
              Your trusted partner for UAE business expansion. Operating from Chennai, we make UAE entrepreneurship accessible to everyone.
            </p>
          </div>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="grid md:grid-cols-3 gap-8 items-start">
              {/* Main Story Content - 2 columns */}
              <div className="md:col-span-2">
                <h2 className="text-4xl font-bold text-gray-800 mb-6">Our Story</h2>
                <div className="space-y-4 text-gray-600 leading-relaxed">
                  <p>
                    Founded in 2010, Firmzy began with a simple mission: to help entrepreneurs realize their dreams of expanding into the UAE market. What started as a small consultancy in Chennai has grown into one of the most trusted business setup advisory firms serving clients globally.
                  </p>
                  <p>
                    Over the past 15 years, we've helped more than 5,000 businesses successfully establish their presence in the UAE. Our deep understanding of UAE business regulations, combined with personalized service, has made us the go-to partner for companies of all sizes.
                  </p>
                  <p>
                    Today, Firmzy offers a comprehensive ecosystem of services including business setup, tax advisory, banking assistance, visa services, and ongoing compliance support. Our team of experts is dedicated to making your UAE expansion journey smooth and successful.
                  </p>
                </div>

                {/* Quick Facts */}
                <div className="mt-8 grid grid-cols-2 gap-4">
                  <div className="bg-gradient-to-br from-purple-50 to-white p-5 rounded-xl border border-purple-100">
                    <div className="text-3xl font-bold text-[#6B1B4D] mb-1">2010</div>
                    <div className="text-sm text-gray-600">Year Founded</div>
                  </div>
                  <div className="bg-gradient-to-br from-blue-50 to-white p-5 rounded-xl border border-blue-100">
                    <div className="text-3xl font-bold text-[#6B1B4D] mb-1">Chennai</div>
                    <div className="text-sm text-gray-600">Head Office</div>
                  </div>
                  <div className="bg-gradient-to-br from-green-50 to-white p-5 rounded-xl border border-green-100">
                    <div className="text-3xl font-bold text-[#6B1B4D] mb-1">5000+</div>
                    <div className="text-sm text-gray-600">Companies Setup</div>
                  </div>
                  <div className="bg-gradient-to-br from-pink-50 to-white p-5 rounded-xl border border-pink-100">
                    <div className="text-3xl font-bold text-[#6B1B4D] mb-1">50+</div>
                    <div className="text-sm text-gray-600">Team Members</div>
                  </div>
                </div>
              </div>

              {/* Side Image - 1 column */}
              <div className="md:col-span-1">
                <img 
                  src="https://images.unsplash.com/photo-1600880292089-90a7e086ee0c?w=400&q=80" 
                  alt="Firmzy Team" 
                  className="rounded-xl shadow-lg w-full h-64 object-cover"
                />
                <div className="mt-4 bg-gradient-to-r from-[#6B1B4D] to-[#DC143C] rounded-xl p-6 text-white">
                  <h3 className="font-bold text-lg mb-2">Our Expertise</h3>
                  <ul className="space-y-2 text-sm">
                    <li className="flex items-center gap-2">
                      <CheckCircle size={16} />
                      UAE Mainland Setup
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle size={16} />
                      50+ Free Zones
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle size={16} />
                      Corporate Tax Advisory
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle size={16} />
                      Visa Processing
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle size={16} />
                      Banking Assistance
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-800 mb-4">Our Values</h2>
            <p className="text-xl text-gray-600">The principles that guide everything we do</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, idx) => {
              const IconComponent = value.icon;
              return (
                <div key={idx} className="bg-white p-8 rounded-xl shadow-lg hover:shadow-2xl transition-all">
                  <div className="w-16 h-16 bg-gradient-to-br from-[#6B1B4D] to-[#DC143C] rounded-lg flex items-center justify-center mb-6">
                    <IconComponent className="text-white" size={32} />
                  </div>
                  <h3 className="text-xl font-bold text-gray-800 mb-3">{value.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{value.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 bg-[#0A1929] text-white">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-4 gap-8">
            {stats.map((stat, idx) => (
              <div key={idx} className="text-center">
                <div className="text-5xl font-bold text-[#DC143C] mb-2">{stat.number}</div>
                <div className="text-gray-300 text-lg">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-800 mb-4">Why Choose Firmzy?</h2>
            <p className="text-xl text-gray-600">What sets us apart in the UAE business setup industry</p>
          </div>
          <div className="grid md:grid-cols-3 gap-6 max-w-6xl mx-auto">
            <div className="bg-gradient-to-br from-blue-50 to-white p-6 rounded-xl border border-blue-100 hover:shadow-xl transition-all">
              <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center mb-4">
                <Globe className="text-white" size={32} />
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-3">Global Reach, Local Expertise</h3>
              <p className="text-gray-600 leading-relaxed text-sm">
                We serve clients worldwide from our Chennai office while maintaining deep expertise in UAE business regulations and market dynamics.
              </p>
            </div>
            <div className="bg-gradient-to-br from-purple-50 to-white p-6 rounded-xl border border-purple-100 hover:shadow-xl transition-all">
              <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg flex items-center justify-center mb-4">
                <TrendingUp className="text-white" size={32} />
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-3">Proven Track Record</h3>
              <p className="text-gray-600 leading-relaxed text-sm">
                With over 5,000 successful company setups and 98% client satisfaction, our results speak for themselves.
              </p>
            </div>
            <div className="bg-gradient-to-br from-pink-50 to-white p-6 rounded-xl border border-pink-100 hover:shadow-xl transition-all">
              <div className="w-16 h-16 bg-gradient-to-br from-pink-500 to-pink-600 rounded-lg flex items-center justify-center mb-4">
                <Users className="text-white" size={32} />
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-3">Dedicated Support</h3>
              <p className="text-gray-600 leading-relaxed text-sm">
                Our team provides end-to-end support from initial consultation to post-setup compliance, ensuring your success every step of the way.
              </p>
            </div>
          </div>

          {/* Additional Benefits Grid */}
          <div className="mt-12 max-w-4xl mx-auto">
            <div className="grid md:grid-cols-2 gap-4">
              <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg">
                <CheckCircle className="text-green-600 flex-shrink-0 mt-1" size={20} />
                <div>
                  <h4 className="font-semibold text-gray-800 mb-1">Transparent Pricing</h4>
                  <p className="text-sm text-gray-600">No hidden fees, clear cost breakdown from the start</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg">
                <CheckCircle className="text-green-600 flex-shrink-0 mt-1" size={20} />
                <div>
                  <h4 className="font-semibold text-gray-800 mb-1">Fast Processing</h4>
                  <p className="text-sm text-gray-600">Quick turnaround times with efficient documentation</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg">
                <CheckCircle className="text-green-600 flex-shrink-0 mt-1" size={20} />
                <div>
                  <h4 className="font-semibold text-gray-800 mb-1">Multilingual Team</h4>
                  <p className="text-sm text-gray-600">Support in English, Hindi, Tamil, Arabic, and more</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg">
                <CheckCircle className="text-green-600 flex-shrink-0 mt-1" size={20} />
                <div>
                  <h4 className="font-semibold text-gray-800 mb-1">Post-Setup Support</h4>
                  <p className="text-sm text-gray-600">Ongoing assistance for compliance and renewals</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-[#6B1B4D] to-[#DC143C] text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-6">Ready to Start Your UAE Journey?</h2>
          <p className="text-xl mb-8">Let's discuss how we can help you expand into the UAE market</p>
          <Link to="/contact">
            <Button 
              size="lg" 
              className="bg-white text-[#6B1B4D] hover:bg-gray-100 px-8 py-6 text-lg"
            >
              Schedule a Free Consultation
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default AboutUs;